// Portal v2 C4: the form-based workflow builder (frontend.md §4c — builder is
// LINEAR and form-based in v1; the node canvas is run visualization only).
// The form emits grammar-2 canonical JSON through ONE serializer
// (serializeWorkflow) and mirrors the server validator's messages client-side
// so save/publish failures read identically wherever they are caught. The
// server (athena/workflow_grammar.py validate_definition) stays authoritative.
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  createWorkflow,
  fetchWorkflowTools,
  publishWorkflow,
  updateWorkflow,
} from "../lib/api";
import { useCan } from "../lib/session";
import type {
  WorkflowCanonical,
  WorkflowDetail,
  WorkflowNode,
  WorkflowTool,
} from "../lib/types";

// The five platform emit points (athena/platform_events.py KNOWN_EVENT_TYPES).
const EVENT_TYPES = [
  "coverage_gap_opened",
  "document_ingested",
  "facts_refreshed",
  "monitor_tripped",
  "observation_persisted",
];

const TEMPLATE_HINT =
  "Text fields accept {{trigger.<key>}}, {{run.input.<key>}} and {{steps.<step_id>.output.<path>}} templates.";

const NODE_ID_RE = /^[A-Za-z0-9_-]+$/;
const TEMPLATE_ONLY_RE = /^\{\{[^{}]+\}\}$/;

// --- form state -----------------------------------------------------------------

type TriggerKind = "manual" | "schedule" | "event";

interface TriggerState {
  kind: TriggerKind;
  cron: string;
  timezone: string;
  eventType: string;
  ticker: string;
}

type StepKind = "tool" | "llm";

interface StepState {
  key: number;
  id: string;
  kind: StepKind;
  tool: string;
  pickerOpen: boolean;
  pickerQuery: string;
  args: Record<string, string>;
  extraArgs: string; // raw-JSON escape hatch for fields the schema form can't render
  userPrompt: string;
  systemPrompt: string;
  modelSelection: string;
}

interface ApprovalState {
  enabled: boolean;
  approvers: string; // comma-separated; blank = run.triggered_by
  expiresHours: string;
  summary: string;
}

interface NotifyState {
  enabled: boolean;
  recipients: string; // comma-separated; blank = run.triggered_by
  subject: string;
  body: string;
}

export interface BuilderState {
  name: string;
  description: string;
  budgetClass: string;
  trigger: TriggerState;
  steps: StepState[];
  approval: ApprovalState;
  notify: NotifyState;
}

let stepKeySeq = 0;

export function blankStep(kind: StepKind, index: number): StepState {
  stepKeySeq += 1;
  return {
    key: stepKeySeq,
    id: `step_${index}`,
    kind,
    tool: "",
    pickerOpen: kind === "tool",
    pickerQuery: "",
    args: {},
    extraArgs: "",
    userPrompt: "",
    systemPrompt: "",
    modelSelection: "",
  };
}

export function blankState(): BuilderState {
  return {
    name: "",
    description: "",
    budgetClass: "standard",
    trigger: { kind: "manual", cron: "", timezone: "UTC", eventType: "", ticker: "" },
    steps: [],
    approval: { enabled: false, approvers: "", expiresHours: "72", summary: "" },
    notify: { enabled: false, recipients: "", subject: "", body: "" },
  };
}

// --- tool input schema rendering ---------------------------------------------------

type FieldKind = "string" | "number" | "boolean" | "enum" | "string[]" | "json";

interface SchemaField {
  name: string;
  kind: FieldKind;
  required: boolean;
  description: string;
  options: string[];
}

export function schemaFields(schema: Record<string, unknown>): SchemaField[] {
  const props = (schema?.properties ?? {}) as Record<string, Record<string, unknown>>;
  const required = new Set(Array.isArray(schema?.required) ? (schema.required as string[]) : []);
  return Object.entries(props).map(([name, def]) => {
    const base = {
      name,
      required: required.has(name),
      description: typeof def?.description === "string" ? def.description : "",
      options: [] as string[],
    };
    const enumValues = def?.enum;
    if (Array.isArray(enumValues) && enumValues.length > 0 && enumValues.every((v) => typeof v === "string")) {
      return { ...base, kind: "enum" as const, options: enumValues as string[] };
    }
    const type = def?.type;
    if (type === "string") return { ...base, kind: "string" as const };
    if (type === "number" || type === "integer") return { ...base, kind: "number" as const };
    if (type === "boolean") return { ...base, kind: "boolean" as const };
    if (type === "array") {
      const items = def?.items as Record<string, unknown> | undefined;
      if (items?.type === "string" || Array.isArray(items?.enum)) {
        return { ...base, kind: "string[]" as const };
      }
    }
    // Unknown/deep shape: per-field raw-JSON fallback (never block the form).
    return { ...base, kind: "json" as const };
  });
}

function coerceArg(
  raw: string,
  kind: FieldKind,
  field: string,
  nodeId: string,
  errors: string[],
): unknown {
  if (TEMPLATE_ONLY_RE.test(raw.trim())) {
    // A single-template string keeps the resolved value's type at run time.
    return raw.trim();
  }
  switch (kind) {
    case "number": {
      const value = Number(raw);
      if (Number.isNaN(value)) {
        errors.push(`tool node '${nodeId}' argument '${field}' must be a number`);
        return undefined;
      }
      return value;
    }
    case "boolean":
      return raw === "true";
    case "string[]":
      return raw
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);
    case "json":
      try {
        return JSON.parse(raw);
      } catch {
        errors.push(`tool node '${nodeId}' argument '${field}' is not valid JSON`);
        return undefined;
      }
    default:
      return raw;
  }
}

// --- serializer (the ONE canonical_json emitter) -----------------------------------

export function serializeWorkflow(
  state: BuilderState,
  toolsByName: Map<string, WorkflowTool>,
): { canonical: WorkflowCanonical; errors: string[] } {
  const errors: string[] = [];
  const name = state.name.trim();
  if (!name) errors.push("definition requires a non-empty 'name'");

  // Trigger node — always first (grammar: the trigger must be nodes[0]).
  const triggerConfig: Record<string, unknown> = {};
  if (state.trigger.kind === "schedule") {
    const cron = state.trigger.cron.trim();
    if (!cron) {
      errors.push("trigger node 'trigger' (trigger.schedule) requires a non-empty config.cron");
    } else if (![5, 6].includes(cron.split(/\s+/).length)) {
      errors.push(`trigger node 'trigger' cron '${cron}' must have 5 (or 6) whitespace-separated fields`);
    }
    triggerConfig.cron = cron;
    if (state.trigger.timezone.trim()) triggerConfig.timezone = state.trigger.timezone.trim();
  } else if (state.trigger.kind === "event") {
    if (!state.trigger.eventType) {
      errors.push("trigger node 'trigger' (trigger.event) requires a non-empty config.event_type");
    }
    triggerConfig.event_type = state.trigger.eventType;
    if (state.trigger.ticker.trim()) {
      triggerConfig.filter = { ticker: state.trigger.ticker.trim().toUpperCase() };
    }
  }
  const nodes: WorkflowNode[] = [
    { id: "trigger", type: `trigger.${state.trigger.kind}`, config: triggerConfig },
  ];

  if (state.steps.length === 0) {
    errors.push("definition must contain at least one step node after the trigger");
  }

  const seenIds = new Set(["trigger"]);
  for (const step of state.steps) {
    const nodeId = step.id.trim();
    if (!nodeId) {
      errors.push("every step needs a non-empty id");
      continue;
    }
    if (!NODE_ID_RE.test(nodeId)) {
      errors.push(
        `node id '${nodeId}' is invalid: grammar-2 ids use letters, digits, hyphens, and underscores only (ids appear in dotted template paths)`,
      );
    }
    if (seenIds.has(nodeId)) {
      errors.push(`duplicate node id '${nodeId}'`);
      continue;
    }
    seenIds.add(nodeId);

    if (step.kind === "llm") {
      if (!step.userPrompt.trim()) {
        errors.push(`llm node '${nodeId}' requires a non-empty config.user_prompt_template`);
      }
      const config: Record<string, unknown> = { user_prompt_template: step.userPrompt };
      if (step.systemPrompt.trim()) config.system_prompt = step.systemPrompt;
      if (step.modelSelection.trim()) config.model_selection = step.modelSelection.trim();
      nodes.push({ id: nodeId, type: "llm", config });
      continue;
    }

    if (!step.tool) {
      errors.push(`tool node '${nodeId}' requires a non-empty config.tool`);
      nodes.push({ id: nodeId, type: "agent.tool", config: { tool: "" } });
      continue;
    }
    const tool = toolsByName.get(step.tool);
    const fields = tool ? schemaFields(tool.input_schema) : [];
    const fieldKinds = new Map(fields.map((field) => [field.name, field.kind]));
    const args: Record<string, unknown> = {};
    for (const [field, raw] of Object.entries(step.args)) {
      if (!raw.trim()) continue;
      const value = coerceArg(raw, fieldKinds.get(field) ?? "string", field, nodeId, errors);
      if (value !== undefined) args[field] = value;
    }
    for (const field of fields) {
      if (field.required && args[field.name] === undefined) {
        errors.push(`tool node '${nodeId}' argument '${field.name}' is required`);
      }
    }
    if (step.extraArgs.trim()) {
      try {
        const extra = JSON.parse(step.extraArgs);
        if (extra && typeof extra === "object" && !Array.isArray(extra)) {
          Object.assign(args, extra);
        } else {
          errors.push(`tool node '${nodeId}' additional arguments must be a JSON object`);
        }
      } catch {
        errors.push(`tool node '${nodeId}' additional arguments are not valid JSON`);
      }
    }
    nodes.push({ id: nodeId, type: "agent.tool", config: { tool: step.tool, arguments: args } });
  }

  // Approval (optional, penultimate) must guard a final notify — the grammar
  // rejects an approval without the notify it snapshots.
  if (state.approval.enabled && !state.notify.enabled) {
    errors.push(
      "control.approval node 'approval' must be the penultimate node, immediately before the final notify.email it guards",
    );
  }
  if (state.approval.enabled) {
    const config: Record<string, unknown> = {};
    const approvers = state.approval.approvers
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
    if (approvers.length > 0) {
      if (!approvers.every((email) => email.includes("@"))) {
        errors.push("approval node 'approval' config.approver_emails must be a NON-EMPTY list of email addresses");
      }
      config.approver_emails = approvers;
    } else {
      config.approver_source = "run.triggered_by";
    }
    const hours = state.approval.expiresHours.trim();
    if (hours) {
      const value = Number(hours);
      if (Number.isNaN(value) || value <= 0) {
        errors.push("approval node 'approval' config.expires_hours must be a positive number");
      } else {
        config.expires_hours = value;
      }
    }
    if (state.approval.summary.trim()) config.summary_template = state.approval.summary;
    nodes.push({ id: "approval", type: "control.approval", config });
  }

  if (state.notify.enabled) {
    const config: Record<string, unknown> = {};
    const recipients = state.notify.recipients
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
    if (recipients.length > 0) {
      if (!recipients.every((email) => email.includes("@"))) {
        errors.push("notify node 'notify' config.recipient_emails must be a NON-EMPTY list of email addresses");
      }
      config.recipient_emails = recipients;
    } else {
      config.recipient_source = "run.triggered_by";
    }
    if (!state.notify.subject.trim()) {
      errors.push("notify node 'notify' requires a non-empty config.subject_template");
    }
    if (!state.notify.body.trim()) {
      errors.push("notify node 'notify' requires a non-empty config.body_template");
    }
    config.subject_template = state.notify.subject;
    config.body_template = state.notify.body;
    nodes.push({ id: "notify", type: "notify.email", config });
  }

  const edges: Array<[string, string]> = [];
  for (let i = 1; i < nodes.length; i += 1) {
    edges.push([nodes[i - 1].id, nodes[i].id]);
  }
  const canonical: WorkflowCanonical = { name, nodes, edges };
  if (state.description.trim()) canonical.description = state.description.trim();
  return { canonical, errors };
}

// --- hydration (edit an existing draft) --------------------------------------------

export function hydrateState(detail: WorkflowDetail): BuilderState {
  const state = blankState();
  state.name = detail.name;
  state.description = detail.description ?? "";
  state.budgetClass = detail.budget_class;
  const nodes = detail.canonical_json?.nodes ?? [];
  let stepIndex = 0;
  for (const node of nodes) {
    const config = (node.config ?? {}) as Record<string, unknown>;
    if (node.type.startsWith("trigger.")) {
      const kind = node.type.split(".", 2)[1] as TriggerKind;
      state.trigger.kind = kind === "schedule" || kind === "event" ? kind : "manual";
      state.trigger.cron = String(config.cron ?? "");
      state.trigger.timezone = String(config.timezone ?? "UTC");
      state.trigger.eventType = String(config.event_type ?? "");
      const filter = config.filter as Record<string, unknown> | undefined;
      state.trigger.ticker = filter?.ticker ? String(filter.ticker) : "";
    } else if (node.type === "agent.tool") {
      stepIndex += 1;
      const step = blankStep("tool", stepIndex);
      step.id = node.id;
      step.tool = String(config.tool ?? "");
      step.pickerOpen = !step.tool;
      const args = (config.arguments ?? {}) as Record<string, unknown>;
      for (const [field, value] of Object.entries(args)) {
        if (typeof value === "string") step.args[field] = value;
        else if (Array.isArray(value) && value.every((item) => typeof item === "string")) {
          step.args[field] = value.join(", ");
        } else step.args[field] = JSON.stringify(value);
      }
      state.steps.push(step);
    } else if (node.type === "llm") {
      stepIndex += 1;
      const step = blankStep("llm", stepIndex);
      step.id = node.id;
      step.userPrompt = String(config.user_prompt_template ?? "");
      step.systemPrompt = String(config.system_prompt ?? "");
      step.modelSelection = String(config.model_selection ?? "");
      state.steps.push(step);
    } else if (node.type === "control.approval") {
      state.approval.enabled = true;
      state.approval.approvers = Array.isArray(config.approver_emails)
        ? (config.approver_emails as string[]).join(", ")
        : "";
      state.approval.expiresHours = config.expires_hours != null ? String(config.expires_hours) : "72";
      state.approval.summary = String(config.summary_template ?? "");
    } else if (node.type === "notify.email") {
      state.notify.enabled = true;
      state.notify.recipients = Array.isArray(config.recipient_emails)
        ? (config.recipient_emails as string[]).join(", ")
        : "";
      state.notify.subject = String(config.subject_template ?? "");
      state.notify.body = String(config.body_template ?? "");
    }
  }
  return state;
}

// --- component ---------------------------------------------------------------------

function ToolArgField({
  field,
  value,
  onChange,
}: {
  field: SchemaField;
  value: string;
  onChange: (next: string) => void;
}) {
  // One span so the caption stays a single row inside the flex-column label.
  const label = (
    <span>
      {field.name}
      {field.required ? <span className="req">*</span> : null}
      {field.kind === "string[]" ? <span className="hint"> (comma-separated)</span> : null}
      {field.kind === "json" ? <span className="hint"> (raw JSON)</span> : null}
    </span>
  );
  if (field.kind === "enum") {
    return (
      <label>
        {label}
        <select value={value} onChange={(event) => onChange(event.target.value)}>
          <option value="">—</option>
          {field.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </label>
    );
  }
  if (field.kind === "boolean") {
    return (
      <label>
        {label}
        <select value={value} onChange={(event) => onChange(event.target.value)}>
          <option value="">—</option>
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      </label>
    );
  }
  if (field.kind === "json") {
    return (
      <label className="wide">
        {label}
        <textarea
          rows={2}
          value={value}
          placeholder='{"nested": "value"}'
          onChange={(event) => onChange(event.target.value)}
        />
      </label>
    );
  }
  return (
    <label>
      {label}
      <input
        type="text"
        value={value}
        placeholder={field.description || (field.kind === "number" ? "0" : "")}
        title={field.description}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function StepCard({
  step,
  tools,
  onChange,
  onRemove,
}: {
  step: StepState;
  tools: WorkflowTool[];
  onChange: (next: StepState) => void;
  onRemove: () => void;
}) {
  const tool = tools.find((candidate) => candidate.name === step.tool);
  const fields = tool ? schemaFields(tool.input_schema) : [];
  const matches = useMemo(() => {
    const query = step.pickerQuery.trim().toLowerCase();
    const pool = query
      ? tools.filter(
          (candidate) =>
            candidate.name.toLowerCase().includes(query) ||
            candidate.description.toLowerCase().includes(query),
        )
      : tools;
    return pool.slice(0, 8);
  }, [tools, step.pickerQuery]);

  return (
    <div className="step-card">
      <div className="step-head">
        <span className="chip blue">{step.kind === "llm" ? "llm" : "tool"}</span>
        <label className="step-id">
          id
          <input
            type="text"
            value={step.id}
            onChange={(event) => onChange({ ...step, id: event.target.value })}
          />
        </label>
        <button className="btn small" type="button" onClick={onRemove}>
          Remove
        </button>
      </div>

      {step.kind === "llm" ? (
        <div className="formgrid">
          <label className="wide">
            <span>User prompt template<span className="req">*</span></span>
            <textarea
              rows={3}
              className="serif"
              value={step.userPrompt}
              onChange={(event) => onChange({ ...step, userPrompt: event.target.value })}
            />
          </label>
          <label className="wide">
            System prompt (optional)
            <textarea
              rows={2}
              value={step.systemPrompt}
              onChange={(event) => onChange({ ...step, systemPrompt: event.target.value })}
            />
          </label>
          <label>
            Model route (optional)
            <input
              type="text"
              value={step.modelSelection}
              placeholder="default route"
              onChange={(event) => onChange({ ...step, modelSelection: event.target.value })}
            />
          </label>
          <div className="hint wide">{TEMPLATE_HINT}</div>
        </div>
      ) : step.pickerOpen || !step.tool ? (
        <div>
          <input
            className="searchbox"
            type="text"
            placeholder="Search the allowlisted tools…"
            value={step.pickerQuery}
            onChange={(event) => onChange({ ...step, pickerQuery: event.target.value })}
          />
          <div className="tool-list">
            {matches.map((candidate) => (
              <button
                key={candidate.name}
                type="button"
                className="tool-item"
                onClick={() =>
                  onChange({ ...step, tool: candidate.name, pickerOpen: false, args: {}, extraArgs: "" })
                }
              >
                <span className="mono">{candidate.name}</span>
                {candidate.read_only ? <span className="chip green">read-only</span> : <span className="chip amber">mutating</span>}
                <span className="tool-desc">{candidate.description}</span>
              </button>
            ))}
            {matches.length === 0 ? <div className="empty">No tool matches.</div> : null}
          </div>
        </div>
      ) : (
        <div>
          <div className="step-toolline">
            <span className="mono">{step.tool}</span>
            {tool?.read_only ? (
              <span className="chip green">read-only</span>
            ) : (
              <span className="chip amber">mutating — stubbed in dry runs</span>
            )}
            <button
              className="btn small"
              type="button"
              onClick={() => onChange({ ...step, pickerOpen: true })}
            >
              Change tool
            </button>
          </div>
          {fields.length > 0 ? (
            <div className="formgrid">
              {fields.map((field) => (
                <ToolArgField
                  key={field.name}
                  field={field}
                  value={step.args[field.name] ?? ""}
                  onChange={(next) => onChange({ ...step, args: { ...step.args, [field.name]: next } })}
                />
              ))}
            </div>
          ) : (
            <div className="hint">This tool declares no form-renderable arguments.</div>
          )}
          <details className="extra-args">
            <summary>Additional arguments (raw JSON)</summary>
            <textarea
              rows={2}
              value={step.extraArgs}
              placeholder='{"advanced_flag": true}'
              onChange={(event) => onChange({ ...step, extraArgs: event.target.value })}
            />
          </details>
          <div className="hint">{TEMPLATE_HINT}</div>
        </div>
      )}
    </div>
  );
}

export default function WorkflowBuilder({
  workflowId,
  initial,
  onClose,
  onSaved,
}: {
  workflowId: string | null; // null = new definition
  initial: WorkflowDetail | null;
  onClose: () => void;
  onSaved: (workflowId: string) => void;
}) {
  const canWrite = useCan("workflows.write");
  const queryClient = useQueryClient();
  const [state, setState] = useState<BuilderState>(() =>
    initial ? hydrateState(initial) : blankState(),
  );
  const [clientErrors, setClientErrors] = useState<string[]>([]);
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [failure, setFailure] = useState<string | null>(null);

  const toolsQuery = useQuery({ queryKey: ["workflow-tools"], queryFn: fetchWorkflowTools });
  const tools = useMemo(() => toolsQuery.data?.tools ?? [], [toolsQuery.data?.tools]);
  const toolsByName = useMemo(
    () => new Map(tools.map((tool) => [tool.name, tool])),
    [tools],
  );

  const save = useMutation({
    mutationFn: async ({ publish }: { publish: boolean }) => {
      const { canonical, errors } = serializeWorkflow(state, toolsByName);
      setClientErrors(errors);
      setServerErrors([]);
      setFailure(null);
      if (errors.length > 0) throw new ApiError(0, "fix the highlighted problems first");
      const request = {
        definition: canonical,
        description: canonical.description ?? null,
        budget_class: state.budgetClass,
      };
      const saved = workflowId
        ? await updateWorkflow(workflowId, request)
        : await createWorkflow(request);
      if (publish) {
        await publishWorkflow(saved.workflow.workflow_id);
      }
      return saved.workflow.workflow_id;
    },
    onSuccess: (savedId) => {
      void queryClient.invalidateQueries({ queryKey: ["workflows"] });
      onSaved(savedId);
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        if (error.errors.length > 0) setServerErrors(error.errors);
        else if (error.status !== 0) setFailure(error.message);
      } else {
        setFailure((error as Error).message);
      }
    },
  });

  const set = (patch: Partial<BuilderState>) => setState((prev) => ({ ...prev, ...patch }));
  const allErrors = [...clientErrors, ...serverErrors];

  return (
    <section className="card builder">
      <div className="builder-head">
        <h2>{workflowId ? `Edit draft — ${initial?.name ?? ""} v${initial?.version ?? ""}` : "New workflow"}</h2>
        <button className="btn" type="button" onClick={onClose}>
          Back to workflows
        </button>
      </div>

      {initial && !initial.validation.valid ? (
        <div className="wf-errors">
          Stored draft currently fails validation:
          <ul>
            {initial.validation.errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="formgrid">
        <label>
          <span>Name<span className="req">*</span></span>
          <input
            type="text"
            value={state.name}
            onChange={(event) => set({ name: event.target.value })}
          />
        </label>
        <label>
          Budget class
          <select
            value={state.budgetClass}
            onChange={(event) => set({ budgetClass: event.target.value })}
          >
            <option value="light">light</option>
            <option value="standard">standard</option>
            <option value="heavy">heavy</option>
          </select>
        </label>
        <label className="wide">
          Description
          <textarea
            rows={2}
            value={state.description}
            onChange={(event) => set({ description: event.target.value })}
          />
        </label>
      </div>

      <h3>Trigger</h3>
      <div className="tabchips">
        {(["manual", "schedule", "event"] as TriggerKind[]).map((kind) => (
          <button
            key={kind}
            type="button"
            className={state.trigger.kind === kind ? "active" : ""}
            onClick={() => set({ trigger: { ...state.trigger, kind } })}
          >
            {kind === "manual" ? "Manual" : kind === "schedule" ? "Cron" : "Event"}
          </button>
        ))}
      </div>
      {state.trigger.kind === "schedule" ? (
        <div className="formgrid">
          <label>
            <span>Cron expression<span className="req">*</span></span>
            <input
              type="text"
              placeholder="0 7 * * 1-5"
              value={state.trigger.cron}
              onChange={(event) => set({ trigger: { ...state.trigger, cron: event.target.value } })}
            />
          </label>
          <label>
            Timezone
            <input
              type="text"
              placeholder="UTC"
              value={state.trigger.timezone}
              onChange={(event) =>
                set({ trigger: { ...state.trigger, timezone: event.target.value } })
              }
            />
          </label>
        </div>
      ) : null}
      {state.trigger.kind === "event" ? (
        <div className="formgrid">
          <label>
            <span>Event type<span className="req">*</span></span>
            <select
              value={state.trigger.eventType}
              onChange={(event) =>
                set({ trigger: { ...state.trigger, eventType: event.target.value } })
              }
            >
              <option value="">—</option>
              {EVENT_TYPES.map((eventType) => (
                <option key={eventType} value={eventType}>
                  {eventType}
                </option>
              ))}
            </select>
          </label>
          <label>
            Ticker filter (optional)
            <input
              type="text"
              placeholder="TTD"
              value={state.trigger.ticker}
              onChange={(event) =>
                set({ trigger: { ...state.trigger, ticker: event.target.value } })
              }
            />
          </label>
        </div>
      ) : null}

      <h3>Steps</h3>
      {toolsQuery.isLoading ? <div className="empty">Loading the tool registry…</div> : null}
      {state.steps.map((step, index) => (
        <StepCard
          key={step.key}
          step={step}
          tools={tools}
          onChange={(next) =>
            set({ steps: state.steps.map((candidate, i) => (i === index ? next : candidate)) })
          }
          onRemove={() => set({ steps: state.steps.filter((_, i) => i !== index) })}
        />
      ))}
      <div className="filter-row">
        <button
          className="btn"
          type="button"
          onClick={() => set({ steps: [...state.steps, blankStep("tool", state.steps.length + 1)] })}
        >
          + Tool step
        </button>
        <button
          className="btn"
          type="button"
          onClick={() => set({ steps: [...state.steps, blankStep("llm", state.steps.length + 1)] })}
        >
          + LLM step
        </button>
      </div>

      <h3>
        <label className="inline-toggle">
          <input
            type="checkbox"
            checked={state.approval.enabled}
            onChange={(event) =>
              set({ approval: { ...state.approval, enabled: event.target.checked } })
            }
          />{" "}
          Approval gate (optional — parks the run before the final notification)
        </label>
      </h3>
      {state.approval.enabled ? (
        <div className="formgrid">
          <label>
            <span>Approver emails <span className="hint">(comma-separated; blank = whoever triggered the run)</span></span>
            <input
              type="text"
              placeholder="mark@daher.me"
              value={state.approval.approvers}
              onChange={(event) =>
                set({ approval: { ...state.approval, approvers: event.target.value } })
              }
            />
          </label>
          <label>
            Expires after (hours)
            <input
              type="text"
              value={state.approval.expiresHours}
              onChange={(event) =>
                set({ approval: { ...state.approval, expiresHours: event.target.value } })
              }
            />
          </label>
          <label className="wide">
            Summary template (optional)
            <input
              type="text"
              placeholder="Approve sending {{steps.search.output.count}} results?"
              value={state.approval.summary}
              onChange={(event) =>
                set({ approval: { ...state.approval, summary: event.target.value } })
              }
            />
          </label>
        </div>
      ) : null}

      <h3>
        <label className="inline-toggle">
          <input
            type="checkbox"
            checked={state.notify.enabled}
            onChange={(event) => set({ notify: { ...state.notify, enabled: event.target.checked } })}
          />{" "}
          Notify (optional final inbox + email)
        </label>
      </h3>
      {state.notify.enabled ? (
        <div className="formgrid">
          <label>
            <span>Recipients <span className="hint">(comma-separated; blank = whoever triggered the run)</span></span>
            <input
              type="text"
              value={state.notify.recipients}
              onChange={(event) =>
                set({ notify: { ...state.notify, recipients: event.target.value } })
              }
            />
          </label>
          <label className="wide">
            <span>Subject template<span className="req">*</span></span>
            <input
              type="text"
              placeholder="Workflow finished: {{steps.step_1.output.count}} results"
              value={state.notify.subject}
              onChange={(event) => set({ notify: { ...state.notify, subject: event.target.value } })}
            />
          </label>
          <label className="wide">
            <span>Body template<span className="req">*</span></span>
            <textarea
              rows={3}
              value={state.notify.body}
              onChange={(event) => set({ notify: { ...state.notify, body: event.target.value } })}
            />
          </label>
          <div className="hint wide">{TEMPLATE_HINT}</div>
        </div>
      ) : null}

      {allErrors.length > 0 ? (
        <div className="wf-errors">
          {serverErrors.length > 0 ? "The server validator rejected the definition:" : "Fix these before saving:"}
          <ul>
            {allErrors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {failure ? <div className="wf-errors">{failure}</div> : null}

      <div className="filter-row builder-footer">
        <button
          className="btn primary"
          type="button"
          disabled={!canWrite || save.isPending}
          title={canWrite ? "" : "Requires workflows.write"}
          onClick={() => save.mutate({ publish: false })}
        >
          {save.isPending ? "Saving…" : "Save draft"}
        </button>
        <button
          className="btn"
          type="button"
          disabled={!canWrite || save.isPending}
          title={canWrite ? "Save, then validate + publish against the live tool allowlist" : "Requires workflows.write"}
          onClick={() => save.mutate({ publish: true })}
        >
          Save &amp; publish
        </button>
        <span className="hint">
          Publishing validates against the live tool allowlist, supersedes the prior published
          version, and projects the trigger.
        </span>
      </div>
    </section>
  );
}
